import { useState, useMemo } from "react";
import moment from "moment";
import axios from "axios";
import useSWR from "swr";
import { Bar } from "react-chartjs-2";
import { DateRangePicker } from "rsuite";
import { ChartOptions } from "chart.js";

import useUser from "../../../hooks/User";
import useAccessToken from "../../../hooks/AccessToken";
import useCalendarLocale from "../../../hooks/CalendarLocale";
import usePredefinedRanges from "../../../hooks/PredefinedRanges";
import useCheckIsMobile from "../../../hooks/useCheckIsMobile";
import { useChatStore } from "../../../stores/ChatStore";
import { ChatAnalyticSummary } from "../../../interfaces/Chat/Analytic";
import { chatMode } from "../../../functions/chat";
import { useSettingsStore } from "../../../stores/SettingsStore";

const formatDate = (date: Date) => moment(date).format("YYYY-MM-DD");

const createBarChartConfig = (label: string, color: string, data: ChatAnalyticSummary[], key: keyof ChatAnalyticSummary) => {
    const labels = data.map((d) => moment(d.date).format("DD MMM YYYY"));
    const datasets = [
        {
            label,
            backgroundColor: color,
            data: data.map((d) => d[key] as number),
        },
    ];

    const options: ChartOptions<"bar"> = {
        responsive: true,
        plugins: {
            legend: { position: "top", display: false },
            title: { display: false },
        },
        scales: {
            y: { beginAtZero: true },
        },
    };

    return { labels, datasets, options, color };
};

const DailyChatSummary = () => {
    const API_URL = process.env.REACT_APP_API_URL;

    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 29); // karena termasuk hari ini juga
    const [startDate, setStartDate] = useState<Date>(thirtyDaysAgo);
    const [endDate, setEndDate] = useState<Date>(today);

    const user = useUser();
    const { settings } = useSettingsStore()
    const token = useAccessToken();
    const calendarLocale = useCalendarLocale();
    const predefinedRanges = usePredefinedRanges();
    const { isMobile } = useCheckIsMobile();
    const { setWindowActive, setMenuActive, tokenChat, selectedChatPhone } = useChatStore();

    const fetcher = async (url: string) =>
        axios
            .get(url, { headers: { "X-Auth-token": token, 'X-Chat-Auth-Token': tokenChat } })
            .then((res) => res.data.data);

    const { data: summary = [], isValidating: isValidatingSummary, mutate: refreshSummary } = useSWR<ChatAnalyticSummary[]>(
        `${API_URL}v2/chat/summary?as${selectedChatPhone[0].phone}&mode=${chatMode(settings.chat_version)}&dateStart=${formatDate(startDate)}&dateEnd=${formatDate(endDate)}`,
        fetcher,
        { revalidateOnFocus: false, revalidateOnReconnect: false }
    );

    const chartConfigs = {
        countRoom: useMemo(
            () => createBarChartConfig("Total Room", "#2684FF", summary, "count_room"),
            [summary]
        ),
        messageInbound: useMemo(
            () => createBarChartConfig("Total Pesan Masuk", "#29C3BE", summary, "message_inbound"),
            [summary]
        ),
        messageOutbound: useMemo(
            () => createBarChartConfig("Total Pesan Keluar", "#DAA573", summary, "message_outbound"),
            [summary]
        ),
    };

    const handleBackButton = () => {
        setMenuActive('analytics')
        setWindowActive('')
    }

    return (
        <div className="card card-flush w-100 p-10" style={{ maxHeight: "calc(100vh - 1rem)", height: "calc(100vh - 1rem)" }}>
            <div className="d-flex align-items-center mb-10">
                <i className="fas fa-arrow-circle-left d-xl-none me-3" onClick={handleBackButton} role="button" style={{ fontSize: "25px" }}></i>
                <h1 className="text-dark fw-bolder fs-1 m-0">Ringkasan Chat Harian</h1>
            </div>

            <div className="d-flex gap-3 justify-content-end">
                <DateRangePicker
                    style={{ width: "250px" }}
                    cleanable={false}
                    character=" - "
                    format="dd MMM yyyy"
                    locale={calendarLocale}
                    value={[startDate, endDate]}
                    ranges={predefinedRanges}
                    placement="bottomEnd"
                    showOneCalendar={isMobile}
                    editable={false}
                    isoWeek
                    onChange={(value) => {
                        if (value?.length === 2) {
                            setStartDate(value[0]);
                            setEndDate(value[1]);
                        }
                    }}
                />
                <button onClick={() => refreshSummary()} className="btn btn-light btn-sm ms-2">
                    <i className="fas fa-sync-alt p-0" />
                </button>
            </div>

            {
                isValidatingSummary ?
                    <div className="loading-overlay">
                        <div className="loader"></div>
                    </div>
                    :
                    <div className="overflow-auto mt-4">
                        <div className="mb-6">
                            <span className="d-flex align-items-center gap-2 mb-2">
                                <span className="bullet bullet-dot h-10px w-10px" style={{ backgroundColor: chartConfigs.countRoom.color }}></span>
                                <span className="text-gray-800 fw-bolder fs-4">Total Room</span>
                            </span>
                            <Bar
                                options={chartConfigs.countRoom.options}
                                data={{
                                    labels: chartConfigs.countRoom.labels,
                                    datasets: chartConfigs.countRoom.datasets,
                                }}
                                style={{ maxHeight: "400px" }}
                            />
                        </div>
                        <div className="mb-6">
                            <span className="d-flex align-items-center gap-2 mb-2">
                                <span className="bullet bullet-dot h-10px w-10px" style={{ backgroundColor: chartConfigs.messageInbound.color }}></span>
                                <span className="text-gray-800 fw-bolder fs-4">Total Pesan Masuk</span>
                            </span>
                            <Bar
                                options={chartConfigs.messageInbound.options}
                                data={{
                                    labels: chartConfigs.messageInbound.labels,
                                    datasets: chartConfigs.messageInbound.datasets,
                                }}
                                style={{ maxHeight: "400px" }}
                            />
                        </div>
                        <div>
                            <span className="d-flex align-items-center gap-2 mb-2">
                                <span className="bullet bullet-dot h-10px w-10px" style={{ backgroundColor: chartConfigs.messageOutbound.color }}></span>
                                <span className="text-gray-800 fw-bolder fs-4">Total Pesan Keluar</span>
                            </span>
                            <Bar
                                options={chartConfigs.messageOutbound.options}
                                data={{
                                    labels: chartConfigs.messageOutbound.labels,
                                    datasets: chartConfigs.messageOutbound.datasets,
                                }}
                                style={{ maxHeight: "400px" }}
                            />
                        </div>
                    </div>
            }
        </div>
    );
};

export { DailyChatSummary };
